import { lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { useState, useEffect, useRef } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";
import { getUserSubscriptions } from "../utils/subscriptionHelpers";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

// Lazy load components
const Button = lazy(() => import("../components/Button"));
const Card = lazy(() => import("../components/Card"));
const SectionTitle = lazy(() => import("../components/SectionTitle"));
const AuthModal = lazy(() => import("../components/AuthModal"));
import { 
  Sparkles, 
  CheckCircle, 
  Rocket, 
  GraduationCap, 
  DollarSign,
  BookOpen,
  Users,
  Star,
  FileText,
  TrendingUp,
  Clock,
  Globe,
  Library
} from "lucide-react";
import Navbar from "../components/Navbar";

interface FeaturedPartner {
  id: string;
  instructorId: string;
  instructorName: string;
  instructorImage: string;
  description: string;
  coursesCount: number;
  studentsCount: number;
  order: number;
  isActive: boolean;
}

export default function Landing() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [partners, setPartners] = useState<FeaturedPartner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [showWhyCreate, setShowWhyCreate] = useState(false);
  
  // Scroll animations for different sections
  const whyCreateSection = useScrollAnimation({ threshold: 0.2 });
  const servicesSection = useScrollAnimation({ threshold: 0.2 });
  const featuresSection = useScrollAnimation({ threshold: 0.2 });
  const pricingSection = useScrollAnimation({ threshold: 0.2 });
  const partnersSection = useScrollAnimation({ threshold: 0.2 });
  const faqSection = useScrollAnimation({ threshold: 0.2 });

  const handleStartFree = () => {
    if (user) {
      // User is logged in, go to pricing to select package
      navigate("/pricing");
    } else {
      // User not logged in, go to register
      navigate("/register");
    }
  };

  // Defer redirect check to avoid blocking initial render
  useEffect(() => {
    if (!user) return;
    
    const timer = setTimeout(async () => {
      try {
        const subscriptions = await getUserSubscriptions(user.uid);
        if (subscriptions.hasCourse) {
          console.log('[Landing] User has course subscription, redirecting to dashboard');
          navigate('/instructor/dashboard');
        }
      } catch (error) {
        console.error('[Landing] Error checking subscriptions:', error);
      }
    }, 500); // Defer by 500ms to allow page to render first
    
    return () => clearTimeout(timer);
  }, [user, navigate]);

  // Trigger slide-up animation on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWhyCreate(true);
    }, 300); // Delay slightly for smooth appearance
    return () => clearTimeout(timer);
  }, []);

  // Defer loading featured partners to improve initial load time
  useEffect(() => {
    const timer = setTimeout(() => {
      const loadPartners = async () => {
        try {
          const partnersQuery = query(
            collection(db, "featuredPartners"),
            where("isActive", "==", true)
          );
          const snapshot = await getDocs(partnersQuery);
          const partnersData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as FeaturedPartner));
          
          // Sort by order
          partnersData.sort((a, b) => a.order - b.order);
          setPartners(partnersData);
        } catch (error) {
          console.error("Error loading partners:", error);
        }
      };

      loadPartners();
    }, 1000); // Load partners after 1 second
    
    return () => clearTimeout(timer);
  }, []);

  // Auto-slide carousel
  useEffect(() => {
    if (partners.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % partners.length);
    }, 3000); // Slide every 3 seconds

    return () => clearInterval(interval);
  }, [partners.length]);

  // Scroll carousel to current index
  useEffect(() => {
    if (carouselRef.current && partners.length > 0) {
      const cardWidth = 280; // Width of each card + gap
      carouselRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: 'smooth'
      });
    }
  }, [currentIndex, partners.length]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar transparent={true} logoType="course" />

      {/* Hero Banner Section */}
      <section className="relative w-full overflow-hidden">
        {/* Desktop Banner */}
        <div className="hidden md:block w-full">
          <img 
            src="/hero-banner-edumi-pc.jpg" 
            alt="EduMi Hero Banner" 
            className="w-full h-auto object-cover"
            loading="eager"
          />
        </div>
        
        {/* Mobile Banner */}
        <div className="block md:hidden w-full">
          <img 
            src="/hero-banner-edumi-mobile.jpg" 
            alt="EduMi Hero Banner Mobile" 
            className="w-full h-auto object-cover"
            loading="eager"
          />
        </div>
      </section>

      {/* Hidden section to maintain structure */}
      <section className="hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Content */}
            <div className="text-center md:text-left text-white md:pl-8">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/30">
                <Sparkles className="w-4 h-4" />
                <span className="font-semibold text-sm">
                  แพลตฟอร์มสร้างคอร์สออนไลน์อันดับ 1
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Online<br />
                Education
              </h1>
              
              <p className="text-base md:text-lg mb-8 leading-relaxed max-w-lg text-white/90">
                <span className="font-bold bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent">สร้างคอร์สเรียนออนไลน์</span>และ Salepage ได้ง่ายๆ ใน 5 นาที ไม่ต้องเขียนโค้ด ไม่ต้องจ้างทำเว็บ เริ่มสอนและสร้างรายได้ได้ทันที
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                {user ? (
                  <>
                    {/* Show Dashboard button only for Pro/Pro+ users */}
                    {(user.subscriptionTier === 'pro' || user.subscriptionTier === 'pro_plus_monthly' || user.subscriptionTier === 'pro_plus_yearly') && (
                      <a href="/instructor/dashboard" className="w-full sm:w-auto">
                        <button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white hover:shadow-2xl px-8 py-4 text-base font-bold rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-xl">
                          <GraduationCap className="w-5 h-5" />
                          ไปที่แดชบอร์ด
                        </button>
                      </a>
                    )}
                    
                    {/* Show My Course button for all logged-in users */}
                    <a href="/instructor/my-courses" className="w-full sm:w-auto">
                      <button className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white hover:shadow-2xl px-8 py-4 text-base font-bold rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-xl">
                        <Library className="w-5 h-5" />
                        My Course
                      </button>
                    </a>
                  </>
                ) : (
                  <>
                    {/* Show Package button for non-logged-in users */}
                    <a href="/subscription/select?plan" className="w-full sm:w-auto">
                      <button className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white hover:shadow-2xl px-8 py-4 text-base font-bold rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-xl">
                        ดูแพ็คเก็จทั้งหมด
                      </button>
                    </a>
                    <button 
                      onClick={() => {
                        setAuthMode('login');
                        setShowAuthModal(true);
                      }}
                      className="w-full sm:w-auto bg-white/20 hover:bg-white/30 text-white border-2 border-white/50 px-8 py-4 text-base font-semibold rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 backdrop-blur-sm"
                    >
                      เข้าสู่ระบบ
                    </button>
                  </>
                )}
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-white/90">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>ไม่ต้องใช้บัตรเครดิต</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>ยกเลิกได้ทุกเมื่อ</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Support 24/7</span>
                </div>
              </div>
            </div>

            {/* Right Illustration */}
            <div className="relative hidden md:flex items-center justify-center">
              <div className="relative w-full max-w-lg">
                {/* Illustration Container - More Compact */}
                <div className="relative">
                  {/* Content */}
                  <div className="relative">
                    <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border-2 border-blue-400/30">
                      {/* Monitor/Screen Illustration */}
                      <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl p-8 mb-6 relative overflow-hidden">
                        {/* Decorative grid pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="grid grid-cols-8 grid-rows-8 h-full w-full">
                            {[...Array(64)].map((_, i) => (
                              <div key={i} className="border border-blue-400"></div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-6">
                            <div className="w-3 h-3 rounded-full bg-red-400"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                            <div className="w-3 h-3 rounded-full bg-green-400"></div>
                          </div>
                          <div className="space-y-4">
                            <div className="h-4 bg-blue-500/30 rounded w-3/4"></div>
                            <div className="h-4 bg-blue-500/30 rounded w-1/2"></div>
                            <div className="h-32 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                              <GraduationCap className="w-16 h-16 text-white" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="h-16 bg-blue-500/20 rounded-lg"></div>
                              <div className="h-16 bg-cyan-500/20 rounded-lg"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-blue-200">
                          <div className="text-2xl font-bold text-blue-600">12+</div>
                          <div className="text-sm text-gray-600 font-medium">คอร์ส</div>
                        </div>
                        <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-cyan-200">
                          <div className="text-2xl font-bold text-cyan-600">248</div>
                          <div className="text-sm text-gray-600 font-medium">นักเรียน</div>
                        </div>
                        <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-blue-200">
                          <div className="text-2xl font-bold text-blue-600">4.9</div>
                          <div className="text-sm text-gray-600 font-medium">คะแนน</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Floating Elements */}
                    <div className="absolute -top-6 -right-6 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-4 shadow-2xl animate-bounce">
                      <Star className="w-8 h-8 text-white fill-white" />
                    </div>
                    <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-2xl p-4 shadow-2xl">
                      <Rocket className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CREATE COURSE SECTION */}
      <section 
        ref={whyCreateSection.elementRef}
        className="pt-32 pb-16 md:pb-20 px-4 bg-gradient-to-b from-white to-blue-50/30"
      >
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-12 scroll-fade-in-up ${showWhyCreate ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-textDark mb-4">
              ทำไมต้องสร้างคอร์สเรียน?
            </h2>
            <p className="text-lg text-textLight max-w-2xl mx-auto">
              แชร์ความรู้ของคุณ สร้างรายได้เสริม และเข้าถึงนักเรียนทั่วโลก
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Benefit 1: Extra Income */}
            <div className={`bg-white rounded-2xl p-8 border border-green-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in scroll-delay-100 ${showWhyCreate ? 'is-visible' : ''}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                สร้างรายได้เสริม
              </h3>
              <p className="text-gray-600 leading-relaxed">
                <span className="font-bold text-green-600">รายได้ 100% เป็นของคุณ</span> ไม่มีค่าคอมมิชชั่น ตั้งราคาเองได้ รับเงินตรงจากนักเรียน
              </p>
            </div>

            {/* Benefit 2: Flexible Time */}
            <div className={`bg-white rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in scroll-delay-200 ${showWhyCreate ? 'is-visible' : ''}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                ทำงานเวลาไหนก็ได้
              </h3>
              <p className="text-gray-600 leading-relaxed">
                <span className="font-bold text-blue-600">อิสระเต็มที่</span> สอนเมื่อไหร่ก็ได้ ที่ไหนก็ได้ ไม่ต้องเสียเวลาเดินทาง ทำงานจากบ้านได้
              </p>
            </div>

            {/* Benefit 3: Global Reach */}
            <div className={`bg-white rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in scroll-delay-300 ${showWhyCreate ? 'is-visible' : ''}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                เข้าถึงทั่วโลก
              </h3>
              <p className="text-gray-600 leading-relaxed">
                <span className="font-bold text-purple-600">ไม่จำกัดจำนวนนักเรียน</span> แชร์ความรู้ให้คนทั่วประเทศ สร้างชื่อเสียงในวงการ
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TWO MAIN SERVICES SECTION - Horizontal Layout with Curved Backgrounds */}
      <section 
        ref={servicesSection.elementRef}
        className="py-16 md:py-20 px-4 bg-gray-50 relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-12 scroll-fade-in-up ${servicesSection.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              2 บริการหลักของเรา
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              เลือกบริการที่เหมาะกับคุณ หรือใช้ทั้ง 2 บริการร่วมกัน
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {/* Course Creation Card - Purple/Fuchsia with Curved Background (3 columns) */}
            <div className={`md:col-span-3 relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in-left scroll-delay-200 ${servicesSection.isVisible ? 'is-visible' : ''}`}>
              {/* Curved Background */}
              <div className="absolute inset-0 overflow-hidden">
                <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 600 400" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="courseGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#8B5CF6', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#6366F1', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <path d="M0,0 L600,0 L600,280 Q450,250 300,280 T0,280 Z" fill="url(#courseGradient)"/>
                </svg>
              </div>
              
              <div className="relative z-10 p-6 md:p-8">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  {/* Left Content */}
                  <div>
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                      <GraduationCap className="w-4 h-4 text-white" />
                      <span className="text-xs font-semibold text-white">Online Learning</span>
                    </div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Online<br />Education
                    </h3>
                    
                    <p className="text-white/90 text-sm mb-4 leading-relaxed">
                      สร้างคอร์สเรียนออนไลน์ได้ไม่จำกัด อัปโหลดวิดีโอ จัดการนักเรียน ออกใบประกาศนียบัตร และรับชำระเงินออนไลน์
                    </p>
                    
                    <a href="/subscription/select?plan">
                      <button className="w-full bg-white hover:bg-gray-50 text-purple-600 px-6 py-3 text-base font-bold rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 shadow-lg">
                        เริ่มสร้างคอร์ส
                      </button>
                    </a>
                  </div>
                  
                  {/* Right Illustration */}
                  <div className="hidden md:block">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-32 h-32 bg-white/20 rounded-2xl flex items-center justify-center">
                          <GraduationCap className="w-16 h-16 text-white" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-2 bg-white/30 rounded w-3/4 mx-auto"></div>
                        <div className="h-2 bg-white/30 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Salepage Card - Emerald/Cyan with Curved Background (2 columns) */}
            <div className={`md:col-span-2 relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in-right scroll-delay-400 ${servicesSection.isVisible ? 'is-visible' : ''}`}>
              {/* Curved Background */}
              <div className="absolute inset-0 overflow-hidden">
                <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="salepageGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#10B981', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#0891B2', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <path d="M0,0 L400,0 L400,280 Q300,250 200,280 T0,280 Z" fill="url(#salepageGradient)"/>
                </svg>
              </div>
              
              <div className="relative z-10 p-6 md:p-8 h-full flex flex-col">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 w-fit">
                  <FileText className="w-4 h-4 text-white" />
                  <span className="text-xs font-semibold text-white">E-learning Program</span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Study<br />Programs
                </h3>
                
                <p className="text-white/90 text-sm mb-4 leading-relaxed flex-grow">
                  สร้างหน้าขายสินค้าหรือบริการได้ง่ายๆ แค่ลากวาง ใส่วิดีโอ รูปภาพ และแชร์ลิงก์ได้ทันที ทดลองใช้ฟรี 7 วัน
                </p>
                
                {/* Illustration */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-4 border border-white/20">
                  <div className="flex items-center justify-center mb-3">
                    <div className="w-20 h-20 bg-white/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-white/30 rounded w-3/4 mx-auto"></div>
                    <div className="h-2 bg-white/30 rounded w-1/2 mx-auto"></div>
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES - Modern Design */}
      <section 
        ref={featuresSection.elementRef}
        id="create-course" 
        className="py-12 md:py-20 px-4 bg-gradient-to-b from-blue-50/30 to-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-10 md:mb-16 scroll-fade-in-up ${featuresSection.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-textDark mb-3 md:mb-4">
              ทำไมต้องเลือก <span className="bg-gradient-primary bg-clip-text text-transparent">EduMi</span>
            </h2>
            <p className="text-base md:text-xl text-textLight max-w-2xl mx-auto px-4">
              ทุกอย่างที่คุณต้องการสำหรับสร้างคอร์สออนไลน์ที่ประสบความสำเร็จ
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {/* Feature 1 */}
            <div className={`group bg-white rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in scroll-delay-100 ${featuresSection.isVisible ? 'is-visible' : ''}`}>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Rocket className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-textDark mb-2 md:mb-3">
                เริ่มสอนได้ทันที
              </h3>
              <p className="text-sm md:text-base text-textLight leading-relaxed">
                ระบบพร้อมใช้งานทันที ไม่ต้องรอ ไม่ต้องติดตั้ง เริ่มสร้างคอร์สได้ใน 5 นาที
              </p>
            </div>

            {/* Feature 2 */}
            <div className={`group bg-white rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 scroll-fade-in scroll-delay-200 ${featuresSection.isVisible ? 'is-visible' : ''}`}>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <GraduationCap className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-textDark mb-2 md:mb-3">
                แพลตฟอร์มมืออาชีพ
              </h3>
              <p className="text-sm md:text-base text-textLight leading-relaxed">
                ช่วยอำนวยความสะดวกให้นักเรียนคุณมีแพลตฟอร์มเรียนออนไลน์ระดับมืออาชีพ
              </p>
            </div>

            {/* Feature 3 */}
            <div className={`group bg-white rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 sm:col-span-2 md:col-span-1 scroll-fade-in scroll-delay-300 ${featuresSection.isVisible ? 'is-visible' : ''}`}>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <DollarSign className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-textDark mb-2 md:mb-3">
                รายได้เป็นของคุณ
              </h3>
              <p className="text-sm md:text-base text-textLight leading-relaxed">
                ไม่มีหักส่วนแบ่ง คุณตั้งราคาเอง รายได้ 100% เป็นของคุณทั้งหมด
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING - Modern Design with Curved Backgrounds */}
      <section 
        ref={pricingSection.elementRef}
        className="pt-12 pb-0 md:pt-16 md:pb-0 px-4 bg-white relative overflow-hidden"
      >
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-12 scroll-fade-in-up ${pricingSection.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              เริ่มต้นฟรี อัปเกรดเมื่อพร้อม
            </h2>
            <p className="text-lg text-gray-600">
              เลือกแพ็กเกจที่เหมาะกับคุณ ไม่มีค่าใช้จ่ายล่วงหน้า
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Free Plan - Light Gray with Subtle Curve */}
            <div className={`relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-200 scroll-scale-in scroll-delay-200 ${pricingSection.isVisible ? 'is-visible' : ''}`}>
              {/* Subtle Background Pattern */}
              <div className="absolute inset-0 overflow-hidden opacity-5">
                <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 500" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0,0 L400,0 L400,350 Q300,320 200,350 T0,350 Z" fill="#6B7280"/>
                </svg>
              </div>
              
              <div className="relative z-10 p-8">
                <div className="text-center mb-6">
                  <p className="text-gray-600 text-sm">สำหรับทดสอบระบบ</p>
                </div>
                
                <div className="text-center mb-8">
                  <span className="text-5xl font-bold text-gray-900">FREE 14 วัน</span>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">สร้างคอร์สได้ 1 คอร์ส</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">นักเรียนสูงสุด 20 คน</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">พื้นที่จัดเก็บเริ่มต้น 5 GB</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">ฟีเจอร์พื้นฐานครบ</span>
                  </li>
                </ul>

                <button 
                  onClick={() => {
                    // Save checkout data for Free trial
                    const checkoutData = {
                      plan: 'free',
                      planName: 'FREE 14 วัน',
                      planPrice: 0,
                      total: 0,
                      isNewPurchase: true,
                      isFree: true
                    };
                    localStorage.setItem('subscriptionCheckout', JSON.stringify(checkoutData));
                    navigate('/subscription/checkout-new?tier=free');
                  }}
                  className="w-full bg-blue-100 hover:bg-blue-200 text-blue-900 py-4 rounded-xl font-bold transition-all duration-300 hover:scale-105"
                >
                  ทดลองใช้ฟรีทันที
                </button>
              </div>
            </div>

            {/* Pro Plan - Dark Blue Tech Theme */}
            <div className={`relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 scroll-scale-in scroll-delay-400 ${pricingSection.isVisible ? 'is-visible' : ''}`}>
              {/* Curved Background with Patterns */}
              <div className="absolute inset-0 overflow-hidden">
                <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 500" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="pricingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#1e3a8a', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#1e40af', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <path d="M0,0 L400,0 L400,500 L0,500 Z" fill="url(#pricingGradient)"/>
                  
                  {/* Geometric Lines */}
                  <line x1="0" y1="150" x2="150" y2="150" stroke="white" strokeWidth="2" opacity="0.1"/>
                  <line x1="250" y1="300" x2="400" y2="300" stroke="white" strokeWidth="2" opacity="0.1"/>
                  <line x1="100" y1="0" x2="100" y2="100" stroke="white" strokeWidth="2" opacity="0.08"/>
                  
                  {/* Diagonal Lines */}
                  <line x1="0" y1="0" x2="100" y2="100" stroke="white" strokeWidth="1.5" opacity="0.06"/>
                  <line x1="300" y1="200" x2="400" y2="300" stroke="white" strokeWidth="1.5" opacity="0.06"/>
                </svg>
              </div>
              
              {/* Badge - Ribbon Style */}
              <div className="absolute top-0 right-0 z-20">
                <div className="relative">
                  {/* Ribbon */}
                  <div className="bg-gradient-to-r from-yellow-400 to-blue-500 px-6 py-2 pr-8 shadow-lg">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-white fill-white" />
                      <span className="text-sm font-bold text-white">แนะนำ</span>
                    </div>
                  </div>
                  {/* Ribbon Fold */}
                  <div className="absolute top-full right-0 w-0 h-0 border-t-[12px] border-t-orange-600 border-r-[12px] border-r-transparent"></div>
                </div>
              </div>

              <div className="relative z-10 p-8 pt-12">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
                  <p className="text-white/90 text-sm">เริ่มต้นอย่างจริงจัง</p>
                </div>
                
                <div className="text-center mb-8">
                  <span className="text-5xl font-bold text-white">฿690</span>
                  <span className="text-white/90 text-base ml-2">/ เดือน</span>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-white">สร้างคอร์สได้ไม่จำกัด</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-white">รับนักเรียนได้ไม่จำกัด</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-white">พื้นที่จัดเก็บเริ่มต้น 20 GB</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-white">ฟีเจอร์พื้นฐานครบ</span>
                  </li>
                </ul>

                <Link to="/subscription/select?plan=pro" className="block">
                  <button className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white py-4 rounded-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg">
                    เริ่มใช้ Pro →
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED INSTRUCTORS / PARTNERS */}
      {partners.length > 0 && (
      <section 
        ref={partnersSection.elementRef}
        className="-mt-8 md:-mt-12 pt-0 pb-12 md:pt-0 md:pb-16 px-4 bg-gradient-to-br from-purple-50 via-white to-pink-50"
      >
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-12 md:mb-16 scroll-fade-in-up ${partnersSection.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-5xl font-bold text-textDark mb-4">
              พาร์ทเนอร์ของเรา
            </h2>
            <p className="text-lg md:text-xl text-textLight max-w-2xl mx-auto">
              ผู้สอนที่ใช้ EduMi สร้างคอร์สออนไลน์และสอนนักเรียนจริง
            </p>
          </div>

          <div className="text-center mb-8">
            <Link to="/pricing#packages">
              <button className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
                ดูแพ็กเกจทั้งหมด
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </Link>
          </div>

          {/* Partner Divider */}
          <div className="mb-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
            <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              พาร์ทเนอร์ของเรา
            </h3>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
          </div>

          {/* Carousel Container */}
          <div className="relative overflow-hidden">
            <div 
              ref={carouselRef}
              className="flex justify-center gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {partners.map((partner) => (
                <div 
                  key={partner.id}
                  className="flex-shrink-0 w-72 snap-center h-full"
                >
                  <Link to={`/instructor/${partner.instructorId}`} className="h-full block">
                    <div className="relative h-full bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-blue-100/50">
                      {/* Decorative gradient overlay */}
                      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-br from-blue-500/10 to-purple-500/10"></div>
                      
                      {/* Content */}
                      <div className="relative p-5 flex flex-col items-center h-full">
                        {/* Profile Image */}
                        {partner.instructorImage ? (
                          <img 
                            src={partner.instructorImage}
                            alt={partner.instructorName}
                            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg mb-3 group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center text-3xl font-bold text-white mb-3 group-hover:scale-105 transition-transform duration-300 shadow-lg border-4 border-white">
                            {partner.instructorName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        
                        {/* Name */}
                        <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
                          {partner.instructorName}
                        </h3>
                        
                        {/* Description */}
                        <div className="flex-grow flex items-start justify-center mb-3 min-h-[60px]">
                          {partner.description ? (
                            <p className="text-sm text-gray-700 text-center line-clamp-3 px-2 leading-relaxed">
                              {partner.description}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400 text-center px-2 leading-relaxed">
                              ผู้สอนมืออาชีพ
                            </p>
                          )}
                        </div>
                        
                        {/* Stats - Compact horizontal layout */}
                        <div className="flex items-center justify-center gap-4 w-full mb-3">
                          <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm">
                            <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                            <span className="text-xs font-semibold text-gray-700">{partner.coursesCount} คอร์ส</span>
                          </div>
                          <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm">
                            <Users className="w-3.5 h-3.5 text-purple-600" />
                            <span className="text-xs font-semibold text-gray-700">{partner.studentsCount.toLocaleString()} นักเรียน</span>
                          </div>
                        </div>
                        
                        {/* CTA Button - More prominent */}
                        <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2.5 px-4 rounded-xl text-sm font-bold hover:shadow-lg transition-all transform group-hover:scale-[1.02]">
                          เข้าชมคอร์ส
                        </button>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {partners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-blue-600 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FAQ Section - Popular Questions Only */}
      <section 
        ref={faqSection.elementRef}
        className="py-16 md:py-20 px-4 bg-gradient-to-br from-gray-50 to-purple-50/30"
      >
        <div className="max-w-5xl mx-auto">
          <div className={`text-center mb-12 scroll-fade-in-up ${faqSection.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-textDark mb-3">
              คำถามยอดนิยม
            </h2>
            <p className="text-base md:text-lg text-textLight">
              คำตอบที่คุณต้องรู้ก่อนเริ่มใช้งาน
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Question 1 */}
            <div className={`group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 scroll-fade-in scroll-delay-100 ${faqSection.isVisible ? 'is-visible' : ''}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  นักเรียนเยอะ คิดเงินเพิ่มไหม?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                Pro และ Pro+ รองรับ <span className="font-bold text-green-600">ไม่จำกัดนักเรียน ✨</span> ไม่ต้องกังวลเรื่องจำนวนคน (Pro 20GB, Pro+ 30GB)
              </p>
            </div>

            {/* Question 2 */}
            <div className={`group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 scroll-fade-in scroll-delay-200 ${faqSection.isVisible ? 'is-visible' : ''}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  ถ้าพื้นที่คอร์สเต็มจะเกิดอะไร?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                ยังสอนได้ตามปกติ แค่ไม่สามารถอัปโหลดวิดีโอใหม่ จนกว่าจะลบไฟล์หรือเพิ่มพื้นที่ด้วย Add-on
              </p>
            </div>

            {/* Question 3 */}
            <div className={`group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 scroll-fade-in scroll-delay-300 ${faqSection.isVisible ? 'is-visible' : ''}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  ลบไฟล์แล้วพื้นที่กลับมาไหม?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                <span className="font-bold text-blue-600">กลับมาทันที</span> เมื่อลบวิดีโอหรือไฟล์ออก พื้นที่จะกลับมาใช้งานได้ทันที
              </p>
            </div>

            {/* Question 4 */}
            <div className={`group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 scroll-fade-in scroll-delay-400 ${faqSection.isVisible ? 'is-visible' : ''}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  EduMi เหมาะกับใคร?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                คนที่ต้องการขายความรู้ของตัวเอง ไม่ต้องสร้างเว็บ ไม่โดนหักส่วนแบ่ง
              </p>
            </div>
          </div>

          {/* CTA to Full FAQ */}
          <div className="text-center mt-10">
            <Link to="/faq">
              <button className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <span>ดูคำถามเพิ่มเติม</span>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER - Modern Design */}
      <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 md:py-16 px-4 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12">
            {/* Brand Section */}
            <div className="md:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-2xl bg-gradient-to-r from-blue-400 to-fuchsia-400 bg-clip-text text-transparent">EduMi</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                แพลตฟอร์มสร้างคอร์สออนไลน์และ Salepage ที่ดีที่สุด สร้างได้ง่าย ใช้งานสะดวก เริ่มสอนและสร้างรายได้ได้ทันที
              </p>
              
              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110">
                  <Globe className="w-5 h-5 text-gray-300" />
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110">
                  <Users className="w-5 h-5 text-gray-300" />
                </a>
              </div>
            </div>
            
            {/* Links Sections */}
            <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8">
              {/* Product */}
              <div>
                <h4 className="font-bold text-base mb-4 text-white flex items-center gap-2">
                  <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full"></div>
                  ผลิตภัณฑ์
                </h4>
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <DollarSign className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>แพ็กเกจราคา</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/register" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <Rocket className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>สมัครใช้งาน</span>
                    </Link>
                  </li>
                  <li>
                    <a href="/#create-course" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <BookOpen className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>สร้างคอร์ส</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className="font-bold text-base mb-4 text-white flex items-center gap-2">
                  <div className="w-1 h-5 bg-gradient-to-b from-emerald-500 to-cyan-600 rounded-full"></div>
                  บริษัท
                </h4>
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link to="/instructor/dashboard" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <Users className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>เกี่ยวกับเรา</span>
                    </Link>
                  </li>
                  <li>
                    <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <Globe className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>ติดต่อเรา</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Support */}
              <div>
                <h4 className="font-bold text-base mb-4 text-white flex items-center gap-2">
                  <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full"></div>
                  ช่วยเหลือ
                </h4>
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link to="/faq" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <CheckCircle className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>คำถามที่พบบ่อย</span>
                    </Link>
                  </li>
                  <li>
                    <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <Star className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>Support</span>
                    </a>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group">
                      <TrendingUp className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>สถิติการใช้งาน</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-gray-400 text-sm">
                &copy; {new Date().getFullYear()} <span className="font-semibold text-white">EduMi</span>. All rights reserved.
              </p>
              
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white transition-colors">นโยบายความเป็นส่วนตัว</a>
                <a href="#" className="hover:text-white transition-colors">เงื่อนไขการใช้งาน</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <Suspense fallback={null}>
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
        />
      </Suspense>
    </div>
  );
}
